import { ImagePlus, LoaderCircle, Sparkles, UploadCloud, WandSparkles } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { api, authConfig, formatApiErrorDetail } from "@/lib/api";

const fieldClass =
  "w-full rounded-[20px] border border-[#001935]/10 bg-white px-4 py-3 text-sm text-[#001935] outline-none transition placeholder:text-[#8c8578] focus:border-[#D4AF37]/60 focus:ring-2 focus:ring-[#D4AF37]/20";
const styleLabels = {
  modern: "Modern",
  luxury: "Luxury",
};
const optimizeCloudinaryUrl = (url, width = 1800) => {
  if (!url.includes("/upload/")) return url;
  return url.replace("/upload/", `/upload/f_auto,q_auto,w_${width},c_limit/`);
};

export const AdminVirtualStagingPanel = ({ editingListingId, listingForm, onListingUpdate, onPersisted }) => {
  const fileInputRef = useRef(null);
  const [modelChoice, setModelChoice] = useState("flux_dev");
  const [stagingMode, setStagingMode] = useState("virtual_staging");
  const [styleChoice, setStyleChoice] = useState("modern");
  const [loading, setLoading] = useState(false);
  const [uploadingSource, setUploadingSource] = useState(false);
  const [lastConfig, setLastConfig] = useState(null);
  const [jobStatus, setJobStatus] = useState("");
  const isExteriorMode = stagingMode === "exterior_renovation";

  const uploadSourceImage = async (file) => {
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG and PNG files are allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be 10MB or smaller");
      return;
    }

    setUploadingSource(true);
    try {
      const signaturePayload = await api.get("/cloudinary/signature", authConfig({ params: { resource_type: "image", folder: "uploads/listings/staging-source" } }));
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", signaturePayload.data.api_key);
      formData.append("timestamp", String(signaturePayload.data.timestamp));
      formData.append("signature", signaturePayload.data.signature);
      formData.append("folder", signaturePayload.data.folder);

      const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signaturePayload.data.cloud_name}/image/upload`, {
        method: "POST",
        body: formData,
      });
      const asset = await cloudinaryResponse.json();
      if (!cloudinaryResponse.ok) {
        throw new Error(asset.error?.message || "Cloudinary upload failed");
      }

      onListingUpdate({
        ...listingForm,
        staging_before_url: optimizeCloudinaryUrl(asset.secure_url, 1600),
        empty_image_url: optimizeCloudinaryUrl(asset.secure_url, 1600),
      });
      toast.success("Source image uploaded successfully");
    } catch (error) {
      toast.error(error.message || "Unable to upload the source image");
    } finally {
      setUploadingSource(false);
    }
  };

  const pollJobUntilDone = async (jobId) => {
    let attempts = 0;
    while (attempts < 40) {
      attempts += 1;
      await new Promise((resolve) => setTimeout(resolve, 4000));
      const pollResponse = await api.get(`/listings/virtual-staging/jobs/${jobId}`, authConfig());
      const payload = pollResponse.data;
      setJobStatus(payload.status);

      if (payload.status === "completed") {
        onListingUpdate({
          ...listingForm,
          staging_after_url: payload.generated_image_url,
          staged_image_url: payload.generated_image_url,
        });
        setLastConfig(payload.applied_config);
        if (payload.persisted_to_listing && payload.listing) {
          onPersisted?.(payload.listing);
        }
        toast.success(payload.persisted_to_listing ? "Staged image generated and saved to the listing" : "Staged image generated — save the listing to persist it");
        setJobStatus("");
        return;
      }

      if (payload.status === "failed") {
        setJobStatus("");
        throw new Error(payload.message || "Virtual staging job failed");
      }
    }

    setJobStatus("");
    throw new Error("Virtual staging is still processing. Please try again shortly.");
  };

  const generateImage = async () => {
    if (!listingForm.empty_image_url) {
      toast.error("Upload the source image first");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        source_image_url: listingForm.staging_before_url || listingForm.empty_image_url,
        model_choice: modelChoice,
        staging_mode: stagingMode,
        style_choice: styleChoice,
        listing_title: listingForm.title,
        listing_id: editingListingId || undefined,
        persist_to_listing: Boolean(editingListingId),
      };
      const response = await api.post("/listings/virtual-staging/generate", payload, authConfig());
      setLastConfig(response.data.applied_config);

      if (response.data.status === "queued" && response.data.job_id) {
        setJobStatus("queued");
        toast.success("Renovation preview queued — preparing final render now");
        await pollJobUntilDone(response.data.job_id);
      } else {
        const generatedUrl = response.data.generated_image_url;
        onListingUpdate({
          ...listingForm,
          staging_after_url: generatedUrl,
          staged_image_url: generatedUrl,
        });
        if (response.data.persisted_to_listing && response.data.listing) {
          onPersisted?.(response.data.listing);
        }
        toast.success(response.data.persisted_to_listing ? "Staged image generated and saved to the listing" : "Staged image generated — save the listing to persist it");
      }
    } catch (error) {
      toast.error(error.message || formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5 rounded-[26px] border border-[#D4AF37]/25 bg-[#fffaf1] p-5" data-testid="admin-virtual-staging-panel">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">AI Virtual Staging</p>
        <h3 className="mt-2 text-2xl font-semibold text-[#001935]">Generate staged or renovated interiors from empty HDB photos.</h3>
        <p className="mt-3 text-sm leading-7 text-[#42566f]">
          Uses your dual-control workflow with structure preservation first. Flux.1 Dev and SDXL 1.0 are both available.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2" data-testid="admin-staging-upload-shell">
          <input
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            className="hidden"
            data-testid="admin-staging-file-input"
            onChange={(event) => {
              uploadSourceImage(event.target.files?.[0]);
              event.target.value = "";
            }}
            ref={fileInputRef}
            type="file"
          />
          <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
            <button
              className="flex min-h-[172px] w-full flex-col items-center justify-center rounded-[24px] border border-dashed border-[#D4AF37]/45 bg-white px-6 py-8 text-center transition hover:border-[#D4AF37]/80"
              data-testid="admin-staging-upload-button"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              {uploadingSource ? <LoaderCircle className="h-8 w-8 animate-spin text-[#8b6d16]" /> : <UploadCloud className="h-8 w-8 text-[#8b6d16]" />}
              <span className="mt-4 text-base font-semibold text-[#001935]">{uploadingSource ? "Uploading source photo..." : "Upload source photo"}</span>
              <span className="mt-2 max-w-xl text-sm leading-7 text-[#42566f]">JPG / PNG only • max 10MB • uploaded URL automatically maps into the staging engine</span>
              {!uploadingSource ? <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#fff7e2] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#8b6d16]"><ImagePlus className="h-4 w-4" /> Choose File</span> : null}
            </button>

            <div className="overflow-hidden rounded-[24px] border border-[#001935]/10 bg-white" data-testid="admin-staging-upload-preview-card">
              {listingForm.staging_before_url ? (
                <img alt="Uploaded source preview" className="aspect-[1.25/1] w-full object-cover" data-testid="admin-staging-upload-preview-image" src={listingForm.staging_before_url} />
              ) : (
                <div className="flex aspect-[1.25/1] items-center justify-center bg-[#fffaf1] px-6 text-center text-sm leading-7 text-[#6b6252]" data-testid="admin-staging-upload-preview-empty">
                  Upload a source photo to preview it here before generating.
                </div>
              )}
            </div>
          </div>
        </div>

        <input className={fieldClass} data-testid="admin-staging-generated-image-input" onChange={(event) => onListingUpdate({ ...listingForm, staging_after_url: event.target.value, staged_image_url: event.target.value })} placeholder="Generated staged image URL" value={listingForm.staging_after_url || listingForm.staged_image_url} />
        <select className={fieldClass} data-testid="admin-staging-model-select" onChange={(event) => setModelChoice(event.target.value)} value={modelChoice}>
          <option value="flux_dev">Flux.1 Dev</option>
          <option value="sdxl_1">SDXL 1.0</option>
        </select>
        <select className={fieldClass} data-testid="admin-staging-mode-select" onChange={(event) => setStagingMode(event.target.value)} value={stagingMode}>
          <option value="virtual_staging">Virtual Staging</option>
          <option value="renovation_preview">Renovation Preview</option>
          <option value="exterior_renovation">Exterior Enhancement</option>
        </select>
        <div className="grid gap-2 sm:col-span-2" data-testid="admin-staging-style-switch">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Style Logic</p>
          <div className="flex flex-wrap gap-2">
            <button
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${styleChoice === "modern" ? "bg-[#001935] text-[#F5D98C] shadow-[0_12px_30px_rgba(0,25,53,0.18)]" : "border border-[#D4AF37]/35 bg-white text-[#8b6d16]"}`}
              data-testid="admin-staging-style-button-modern"
              disabled={isExteriorMode}
              onClick={() => setStyleChoice("modern")}
              type="button"
            >
              Modern
            </button>
            <button
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${styleChoice === "luxury" ? "bg-[#001935] text-[#F5D98C] shadow-[0_12px_30px_rgba(0,25,53,0.18)]" : "border border-[#D4AF37]/35 bg-white text-[#8b6d16]"}`}
              data-testid="admin-staging-style-button-luxury"
              disabled={isExteriorMode}
              onClick={() => setStyleChoice("luxury")}
              type="button"
            >
              Luxury
            </button>
          </div>
        </div>
      </div>

      {isExteriorMode ? (
        <div className="rounded-[22px] border border-[#D4AF37]/25 bg-white px-4 py-4 text-sm leading-7 text-[#42566f]" data-testid="admin-staging-exterior-prompt-note">
          Exterior mode uses the dedicated architectural enhancement prompt for Jervois Prive-style facade shots and ignores the interior style switch.
        </div>
      ) : null}

      <button className="gold-button w-full" data-testid="admin-staging-generate-button" disabled={loading || uploadingSource || !(listingForm.staging_before_url || listingForm.empty_image_url)} onClick={generateImage} type="button">
        {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <WandSparkles className="mr-2 h-4 w-4" />}
        {loading ? (modelChoice === "sdxl_1" ? "Queueing renovation preview..." : "Generating staging preview...") : editingListingId ? "Generate and Save to Listing" : "Generate Preview"}
      </button>

      {jobStatus ? (
        <div className="rounded-[22px] border border-[#D4AF37]/25 bg-white px-4 py-4 text-sm leading-7 text-[#42566f]" data-testid="admin-staging-job-status">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Render Status</p>
          <p className="mt-2">Current SDXL job state: <strong>{jobStatus}</strong>. You can keep working while the final image is being prepared.</p>
        </div>
      ) : null}

      {lastConfig ? (
        <div className="rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4 text-sm leading-7 text-[#42566f]" data-testid="admin-staging-config-summary">
          <div className="flex items-center gap-2 text-[#8b6d16]"><Sparkles className="h-4 w-4" /><p className="text-xs font-bold uppercase tracking-[0.16em]">Applied Node Configuration</p></div>
          <p className="mt-3">Style: {isExteriorMode ? "Exterior Architectural Prompt" : styleLabels[styleChoice]} • Depth: {lastConfig.flux_depth_weight} • Canny: {lastConfig.flux_canny_weight} • Sampler: {lastConfig.sampler} • Steps: {lastConfig.steps} • CFG: {lastConfig.cfg_scale}</p>
        </div>
      ) : null}

      <BeforeAfterSlider afterImage={listingForm.staging_after_url || listingForm.staged_image_url} beforeImage={listingForm.staging_before_url || listingForm.empty_image_url} title={isExteriorMode ? "Exterior Enhancement Preview" : stagingMode === "renovation_preview" ? `${styleLabels[styleChoice]} Renovation Preview` : `${styleLabels[styleChoice]} Virtual Staging Preview`} />
    </div>
  );
};